import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { formatErr } from "../lib/api";
import { Lock, ArrowLeft } from "lucide-react";
import { T, useI18n } from "../lib/i18n";

// Central role → landing map. Add roles here to route new dashboards.
export const ROLE_ROUTE = {
  admin: "/admin",
  couple: "/couple",
  kitchen: "/kitchen",
  coordinator: "/coordinator",
  dj: "/dj",
  mc: "/mc",
  photographer: "/photographer",
  embroidery: "/embroidery",
  guest: "/guest",
};

export default function StaffLogin() {
  useI18n();
  const { user, loading, loginStaff } = useAuth();
  const [u, setU] = useState("");
  const [p, setP] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  // Wait for auth resolution before redirecting an already-signed-in staffer.
  useEffect(() => {
    if (loading) return;
    if (!user) return;
    const dest = ROLE_ROUTE[user.role] || "/";
    navigate(dest, { replace: true });
  }, [user, loading, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const u2 = await loginStaff(u.trim(), p);
      toast.success(`Welcome, ${u2.name}`);
      // Navigate immediately after login as a safety net (in case the effect runs late).
      const dest = ROLE_ROUTE[u2.role];
      if (dest) navigate(dest, { replace: true });
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-motif">
        <div className="text-motif-muted text-sm tracking-luxe" data-testid="staff-auth-loading">{T("Signing you in…")}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-motif">
      <div className="w-full max-w-sm">
        <Link to="/riyazandaimee" className="inline-flex items-center gap-2 text-motif-muted text-xs tracking-luxe mb-6" data-testid="back-home">
          <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} /> back
        </Link>
        <div className="card-elev p-8 fadein">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-accent-motif flex items-center justify-center">
              <Lock className="w-4 h-4 text-primary-motif" strokeWidth={1.5} />
            </div>
            <div>
              <div className="text-[10px] tracking-luxe text-motif-muted">venuefy</div>
              <h1 className="font-display text-2xl text-motif">{T("Vendor sign in")}</h1>
            </div>
          </div>
          <form onSubmit={submit} className="space-y-3">
            <input
              className="input-motif"
              placeholder={T("Username")}
              value={u}
              onChange={(e) => setU(e.target.value)}
              autoFocus
              autoComplete="username"
              data-testid="staff-username"
            />
            <input
              className="input-motif"
              type="password"
              placeholder={T("Password")}
              value={p}
              onChange={(e) => setP(e.target.value)}
              autoComplete="current-password"
              data-testid="staff-password"
            />
            <button type="submit" disabled={submitting} className="btn-primary w-full" data-testid="staff-login-btn">
              {submitting ? T("Signing in…") : T("Sign in")}
            </button>
          </form>
          <div className="mt-6 text-[11px] text-motif-muted leading-relaxed">
            <div className="tracking-luxe text-[10px] mb-2">{T("team access")}</div>
            <div>admin · kitchen · coordinator · dj</div>
            <div>photographer · embroidery · couple (aimee / riyaz)</div>
          </div>
        </div>
      </div>
    </div>
  );
}
